<template>
  <div class="carded-layout">
    <v-container class="mt-6">
      <v-card class="hero-card" elevation="8">
        <v-img :src="currentVehicleImage" height="400" cover class="hero-image">
          <div class="hero-overlay">
            <v-container>
              <div class="hero-content">
                <v-chip color="white" variant="flat" class="mb-4">
                  {{ modelData.year }} Model Year
                </v-chip>
                <h1 class="hero-title">
                  {{ modelData.make }} {{ modelData.model }}
                </h1>
                <p v-if="selectedTrim" class="hero-trim-name">
                  {{ selectedTrim.trim || selectedTrim.styleName }}
                </p>
                <p class="hero-subtitle">
                  {{
                    selectedTrim
                      ? formatCurrency(
                          selectedTrim.baseMsrp || selectedTrim.price || 0,
                        )
                      : `Starting from ${formatCurrency(lowestPrice)}`
                  }}
                </p>
              </div>
            </v-container>
          </div>
        </v-img>
      </v-card>
    </v-container>

    <v-container class="cards-container">
      <v-row>
        <v-col v-if="availableColors.length > 0" cols="12" md="6" lg="4">
          <v-card class="feature-card" elevation="2">
            <v-card-title class="d-flex align-center">
              <v-icon start>mdi-palette</v-icon>
              Color Options
            </v-card-title>
            <v-card-text>
              <div class="color-showcase">
                <div class="selected-color-display">
                  <div
                    class="color-preview"
                    :style="{
                      backgroundColor:
                        selectedColor?.hex || availableColors[0]?.hex || '#ccc',
                    }"
                  />
                  <span class="color-name">
                    {{ selectedColor?.colorName || 'Select a color' }}
                  </span>
                </div>
                <v-divider class="my-3" />
                <div class="color-options">
                  <button
                    v-for="(color, index) in availableColors"
                    :key="index"
                    class="color-option"
                    :class="{ active: selectedColor?.hex === color.hex }"
                    :style="{ backgroundColor: color.hex || '#ccc' }"
                    @click="$emit('select-color', color)"
                    :aria-label="`Select ${color.colorName}`"
                  >
                    <v-icon
                      v-if="selectedColor?.hex === color.hex"
                      size="x-small"
                      color="white"
                    >
                      mdi-check
                    </v-icon>
                  </button>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="6" lg="4">
          <v-card class="feature-card" elevation="2">
            <v-card-title class="d-flex align-center">
              <v-icon start>mdi-chart-box</v-icon>
              Quick Stats
            </v-card-title>
            <v-card-text>
              <div class="stats-grid">
                <div
                  v-for="stat in quickStats"
                  :key="stat.label"
                  class="stat-item"
                >
                  <v-icon :icon="stat.icon" size="small" color="primary" />
                  <div class="stat-content">
                    <v-tooltip
                      :text="stat.value"
                      location="top"
                      :disabled="stat.value.length <= 15"
                    >
                      <template v-slot:activator="{ props }">
                        <div class="stat-value" v-bind="props">
                          {{ stat.value }}
                        </div>
                      </template>
                    </v-tooltip>
                    <div class="stat-label">{{ stat.label }}</div>
                  </div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="6" lg="4">
          <v-card class="feature-card selected-trim-card" elevation="2">
            <v-card-title class="d-flex align-center">
              <v-icon start>mdi-star</v-icon>
              Selected Trim
            </v-card-title>
            <v-card-text>
              <v-select
                :model-value="selectedTrim"
                :items="modelData.trims"
                :item-title="(item: TrimData) => item.trim || item.styleName"
                :item-value="(item: TrimData) => item"
                @update:model-value="$emit('select-trim', $event)"
                label="Choose trim"
                variant="solo"
                density="compact"
                class="mb-3"
              />
              <div v-if="selectedTrim" class="selected-trim-info">
                <div class="trim-price-display">
                  {{
                    formatCurrency(
                      selectedTrim.baseMsrp || selectedTrim.price || 0,
                    )
                  }}
                </div>
                <v-divider class="my-3" />
                <div class="trim-highlights">
                  <div class="highlight-item">
                    <v-icon size="x-small">mdi-engine</v-icon>
                    {{ getEngineInfo(selectedTrim) }}
                  </div>
                  <div class="highlight-item">
                    <v-icon size="x-small">mdi-gas-station</v-icon>
                    {{ getMpgInfo(selectedTrim) }}
                    {{ isElectricVehicle(selectedTrim) ? 'MPGe' : 'MPG' }}
                  </div>
                  <div class="highlight-item">
                    <v-icon size="x-small">mdi-seat</v-icon>
                    {{ getSeating(selectedTrim) }} Seats
                  </div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12">
          <v-card class="feature-card comparison-card" elevation="2">
            <v-card-title class="d-flex align-center justify-space-between">
              <div class="d-flex align-center">
                <v-icon start>mdi-compare-horizontal</v-icon>
                Compare Trims
              </div>
              <v-chip
                v-if="comparisonTrims.length > 0"
                color="primary"
                size="small"
              >
                {{ comparisonTrims.length }} selected
              </v-chip>
            </v-card-title>
            <v-card-text>
              <v-row>
                <v-col
                  v-for="(trim, index) in modelData.trims"
                  :key="trim.id || index"
                  cols="12"
                  sm="6"
                  md="4"
                  lg="3"
                >
                  <v-card
                    class="trim-card"
                    :class="{
                      selected: selectedTrim?.id === trim.id,
                      'in-comparison': isInComparison(trim.id),
                    }"
                    variant="outlined"
                    @click="$emit('select-trim', trim)"
                  >
                    <v-img
                      :src="trim.img || noVehicleImage"
                      height="140"
                      cover
                      class="trim-image"
                    />
                    <v-card-title class="trim-card-title">
                      {{ trim.trim || trim.styleName }}
                    </v-card-title>
                    <v-card-subtitle class="trim-card-price">
                      {{ formatCurrency(trim.baseMsrp || trim.price || 0) }}
                    </v-card-subtitle>
                    <v-card-text>
                      <div class="trim-features-list">
                        <v-chip
                          v-for="(feature, idx) in getTopFeatures(trim).slice(
                            0,
                            2,
                          )"
                          :key="idx"
                          size="x-small"
                          variant="tonal"
                          class="mr-1 mb-1"
                        >
                          {{ feature }}
                        </v-chip>
                      </div>
                    </v-card-text>
                    <v-card-actions v-if="showTrimComparing !== false">
                      <v-btn
                        :color="isInComparison(trim.id) ? 'success' : 'primary'"
                        :variant="isInComparison(trim.id) ? 'flat' : 'outlined'"
                        size="small"
                        block
                        @click.stop="$emit('add-to-comparison', trim)"
                      >
                        <v-icon start>
                          {{
                            isInComparison(trim.id) ? 'mdi-check' : 'mdi-plus'
                          }}
                        </v-icon>
                        {{ isInComparison(trim.id) ? 'Added' : 'Compare' }}
                      </v-btn>
                    </v-card-actions>
                  </v-card>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col
          v-if="showTrimComparing !== false && comparisonTrims.length > 1"
          cols="12"
        >
          <v-card class="feature-card" elevation="2">
            <v-card-title class="d-flex align-center justify-space-between">
              <div class="d-flex align-center">
                <v-icon start>mdi-table</v-icon>
                Detailed Comparison
              </div>
              <v-btn
                color="error"
                variant="text"
                size="small"
                @click="$emit('clear-comparison')"
              >
                Clear All
              </v-btn>
            </v-card-title>
            <v-card-text class="pa-0">
              <EnhancedTrimComparison
                :trims="comparisonTrims"
                :modelData="modelData"
                @remove-trim="$emit('remove-from-comparison', $event)"
                @clear-comparison="$emit('clear-comparison')"
                @diff-toggle="$emit('diff-toggle', $event)"
              />
            </v-card-text>
          </v-card>
        </v-col>

        <v-col v-if="selectedTrim" cols="12" md="6">
          <v-card class="feature-card" elevation="2">
            <v-card-title class="d-flex align-center">
              <v-icon start>mdi-cog</v-icon>
              {{ selectedTrim.trim || selectedTrim.styleName }} Specifications
            </v-card-title>
            <v-card-text>
              <v-list density="compact">
                <v-list-item
                  v-for="spec in getMainSpecs(selectedTrim)"
                  :key="spec.label"
                >
                  <template v-slot:prepend>
                    <v-icon :icon="spec.icon" size="small" />
                  </template>
                  <v-list-item-title>{{ spec.label }}</v-list-item-title>
                  <template v-slot:append>
                    <span class="spec-value">{{ spec.value }}</span>
                  </template>
                </v-list-item>
              </v-list>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col v-if="selectedTrim" cols="12" md="6">
          <v-card class="feature-card" elevation="2">
            <v-card-title class="d-flex align-center">
              <v-icon start>mdi-star-box</v-icon>
              Key Features
            </v-card-title>
            <v-card-text>
              <div class="features-grid">
                <div
                  v-for="feature in getKeyFeatures(selectedTrim)"
                  :key="feature"
                  class="feature-item"
                >
                  <v-icon size="x-small" color="success"
                    >mdi-check-circle</v-icon
                  >
                  <span>{{ feature }}</span>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <v-container
      v-if="showModelComparing !== false"
      class="model-comparison-container"
    >
      <v-card elevation="2" class="model-comparison-card">
        <v-card-title class="d-flex align-center">
          <v-icon start>mdi-car-compare</v-icon>
          Compare With Other Models
        </v-card-title>
        <v-card-subtitle>
          See how this model stacks up against competitors
        </v-card-subtitle>
        <v-card-text>
          <slot name="model-comparison"></slot>
        </v-card-text>
      </v-card>
    </v-container>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import EnhancedTrimComparison from '@/components/EnhancedTrimComparison.vue'
import { formatCurrency, getCDNUrl } from '@/utils'
import {
  getMpgInfo as getMpgInfoUtil,
  parseSpecItem,
  parseSpecValue,
} from '@/utils/modelDetails'
import type {
  TrimData,
  ColorOption,
  ModelData,
  SpecificationCategory,
} from '@/types'

interface SpecItem {
  label: string
  value: string
  icon: string
}

const props = defineProps<{
  modelData: ModelData
  selectedTrim: TrimData | null
  selectedColor: ColorOption | null
  comparisonTrims: TrimData[]
  availableColors: ColorOption[]
  lowestPrice: number
  currentVehicleImage: string
  showTrimComparing?: boolean
  showModelComparing?: boolean
}>()

defineEmits<{
  'select-trim': [trim: TrimData]
  'select-color': [color: ColorOption]
  'add-to-comparison': [trim: TrimData]
  'remove-from-comparison': [trimId: string]
  'clear-comparison': []
  'diff-toggle': [enabled: boolean]
}>()

const cdnUrl = getCDNUrl()
const noVehicleImage = `${cdnUrl}/lightning-model-page/cx/no-vehicle-image.png`

const quickStats = computed(() => {
  const activeTrim = props.selectedTrim || props.modelData?.trims?.[0]
  return [
    {
      icon: 'mdi-gas-station',
      value: getMpgInfo(activeTrim) || 'N/A',
      label: isElectricVehicle(activeTrim) ? 'MPGe' : 'MPG',
    },
    {
      icon: 'mdi-engine',
      value: getHorsepower(activeTrim) || 'N/A',
      label: 'HP',
    },
    {
      icon: 'mdi-seat',
      value: getSeating(activeTrim) || 'N/A',
      label: 'Seats',
    },
    {
      icon: 'mdi-car-shift-pattern',
      value: getDrivetrain(activeTrim) || 'N/A',
      label: 'Drive',
    },
  ]
})

const isInComparison = (trimId: string): boolean => {
  return props.comparisonTrims.some(t => t.id === trimId)
}

const getSpecValue = (
  trim: TrimData | null | undefined,
  category: string,
  searchTerm: string,
): string => {
  const tech = trim?.technicalSpecification
  const categoryData = tech?.[category] as SpecificationCategory | undefined
  const items = categoryData?.items || []
  const item = items.find((i: string) => i.includes(searchTerm))
  if (item) {
    const value = parseSpecValue(item)
      .replace(/<[^>]*>/g, '')
      .replace(/\s+/g, ' ')
      .trim()
    return value
  }
  return 'N/A'
}

const getEngineInfo = (trim: TrimData | null | undefined): string => {
  const engine = trim?.technicalSpecification?.Engine?.items?.[0]
  if (engine) {
    const match = engine.match(/(\d+\.\d+L|\d+L)/i)
    if (match) return match[0] + ' Engine'
    const parsed = parseSpecItem(engine, false)
    return (typeof parsed === 'object' ? parsed.label : engine) || 'Engine'
  }
  return 'Engine'
}

const getMpgInfo = (trim: TrimData | null | undefined): string => {
  const mpg = getMpgInfoUtil(trim)
  if (mpg === 'MPG N/A' || mpg === 'N/A') return '--'
  const match = mpg.match(/\d+/)
  return match ? match[0] : '--'
}

const isElectricVehicle = (trim: TrimData | null | undefined): boolean => {
  const cityMPGe = getSpecValue(trim, 'Mileage', 'EPA MPG Equivalent - City')
  const hwMPGe = getSpecValue(trim, 'Mileage', 'EPA MPG Equivalent - Hwy')
  return !!(cityMPGe && cityMPGe !== 'N/A') || !!(hwMPGe && hwMPGe !== 'N/A')
}

const getHorsepower = (trim: TrimData | null | undefined): string => {
  let hp = getSpecValue(trim, 'Engine', 'SAE Net Horsepower')
  if (!hp || hp === 'N/A') {
    hp = getSpecValue(trim, 'Engine', 'Horsepower')
  }
  if (!hp || hp === 'N/A') {
    hp = getSpecValue(trim, 'Engine', 'HP')
  }
  if (hp && hp !== 'N/A') {
    const match = hp.match(/\d+/)
    return match ? match[0] : hp.replace(/[^0-9]/g, '')
  }
  return '--'
}

const getSeating = (trim: TrimData | null | undefined): string => {
  const seats =
    getSpecValue(trim, 'Interior Dimensions', 'Passenger') ||
    getSpecValue(trim, 'Summary', 'Passenger')
  if (seats && seats !== 'N/A') {
    const match = seats.match(/\d+/)
    return match ? match[0] : seats.replace(/[^0-9]/g, '')
  }
  return '5'
}

const getDrivetrain = (trim: TrimData | null | undefined): string => {
  const drivetrain =
    getSpecValue(trim, 'Transmission', 'Drivetrain') ||
    getSpecValue(trim, 'Transmission', 'Drive')
  if (drivetrain && drivetrain !== 'N/A') {
    return drivetrain
  }
  return 'FWD'
}

const getTopFeatures = (trim: TrimData | null | undefined): string[] => {
  const features = []

  const engine = trim?.technicalSpecification?.Engine?.items?.[0]
  if (engine) {
    const match = engine.match(/(\d+\.\d+L|\d+L)/i)
    if (match) features.push(match[0])
  }

  const drivetrain = getSpecValue(trim, 'Transmission', 'Drivetrain')
  if (drivetrain !== 'N/A') features.push(drivetrain)

  const mpg = getMpgInfo(trim)
  const mpgLabel = isElectricVehicle(trim) ? 'MPGe' : 'MPG'
  if (mpg !== 'N/A' && mpg !== '--') features.push(`${mpg} ${mpgLabel}`)

  return features
}

const getMainSpecs = (trim: TrimData | null | undefined): SpecItem[] => {
  return [
    {
      icon: 'mdi-engine',
      label: 'Engine',
      value: getSpecValue(trim, 'Engine', 'Engine Type'),
    },
    {
      icon: 'mdi-lightning-bolt',
      label: 'Horsepower',
      value: getSpecValue(trim, 'Engine', 'SAE Net Horsepower'),
    },
    {
      icon: 'mdi-gauge',
      label: 'Torque',
      value: getSpecValue(trim, 'Engine', 'SAE Net Torque'),
    },
    {
      icon: 'mdi-car-shift-pattern',
      label: 'Transmission',
      value: getSpecValue(trim, 'Transmission', 'Trans Description'),
    },
    {
      icon: 'mdi-road-variant',
      label: 'Drivetrain',
      value: getSpecValue(trim, 'Transmission', 'Drivetrain'),
    },
    {
      icon: 'mdi-gas-station',
      label: 'City/Highway MPG',
      value: `${getSpecValue(trim, 'Mileage', 'EPA MPG City')}/${getSpecValue(trim, 'Mileage', 'EPA MPG Highway')}`,
    },
  ].filter(
    spec => spec.value && spec.value !== 'N/A' && spec.value !== 'N/A/N/A',
  )
}

const getKeyFeatures = (trim: TrimData | null | undefined): string[] => {
  const features: string[] = []
  const safetyItems = trim?.standardEquipment?.SAFETY?.items || []
  const techItems = trim?.standardEquipment?.TECHNOLOGY?.items || []
  const comfortItems = trim?.standardEquipment?.COMFORT?.items || []

  safetyItems.slice(0, 4).forEach((item: string) => {
    const parsed = parseSpecItem(item, false)
    const feature = typeof parsed === 'object' ? parsed.label || item : item
    if (feature.length < 30) features.push(feature)
  })

  techItems.slice(0, 3).forEach((item: string) => {
    const parsed = parseSpecItem(item, false)
    const feature = typeof parsed === 'object' ? parsed.label || item : item
    if (feature.length < 30) features.push(feature)
  })

  comfortItems.slice(0, 3).forEach((item: string) => {
    const parsed = parseSpecItem(item, false)
    const feature = typeof parsed === 'object' ? parsed.label || item : item
    if (feature.length < 30) features.push(feature)
  })

  if (features.length === 0 && trim?.technicalSpecification) {
    const specs = trim.technicalSpecification

    if (specs.Engine?.items?.length) {
      features.push(specs.Engine.items[0])
    }

    if (specs.Transmission?.items?.length) {
      specs.Transmission.items.slice(0, 2).forEach((item: string) => {
        if (features.length < 10) features.push(item)
      })
    }

    if (specs.Mileage?.items?.length) {
      specs.Mileage.items.slice(0, 2).forEach((item: string) => {
        if (features.length < 10) features.push(item)
      })
    }

    if (specs['Interior Dimensions']?.items?.length && features.length < 10) {
      const seatingItem = specs['Interior Dimensions'].items.find(
        (item: string) => item.toLowerCase().includes('passenger'),
      )
      if (seatingItem) features.push(seatingItem)
    }
  }

  if (features.length === 0) {
    features.push(
      'Anti-lock Braking System (ABS)',
      'Electronic Stability Control',
      'Traction Control',
      'Air Conditioning',
      'Power Windows',
      'AM/FM Stereo',
    )
  }

  return features.slice(0, 10)
}
</script>

<style scoped>
.carded-layout {
  width: 100%;
  background: var(--v-theme-background);
  padding-bottom: 48px;
}

.hero-card {
  border-radius: 16px;
  overflow: hidden;
}

.hero-image {
  position: relative;
}

.hero-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0.3) 0%,
    rgba(0, 0, 0, 0.7) 100%
  );
  display: flex;
  align-items: flex-end;
  padding: 48px 0;
}

.hero-content {
  color: white;
}

.hero-title {
  font-size: 3rem;
  font-weight: 700;
  margin-bottom: 8px;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
}

.hero-trim-name {
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 8px;
  opacity: 0.95;
  text-shadow: 1px 1px 3px rgba(0, 0, 0, 0.5);
}

.hero-subtitle {
  font-size: 1.5rem;
  opacity: 0.95;
}

.cards-container {
  margin-top: -48px;
  position: relative;
  z-index: 1;
}

.feature-card {
  height: 100%;
  border-radius: 12px;
  transition: all 0.3s ease;
}

.feature-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}

.feature-card :deep(.v-card-title) {
  font-weight: 600;
  font-size: 1.125rem;
}

.color-showcase {
  padding: 8px 0;
}

.selected-color-display {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
}

.color-preview {
  width: 60px;
  height: 60px;
  border-radius: 12px;
  border: 2px solid rgba(0, 0, 0, 0.1);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.color-name {
  font-weight: 500;
  font-size: 1.125rem;
  text-transform: capitalize;
}

.color-options {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.color-option {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  border: 2px solid transparent;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.color-option:hover {
  transform: scale(1.15);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

.color-option.active {
  border-color: rgb(var(--v-theme-primary));
  transform: scale(1.2);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: var(--v-theme-surface-variant);
  border-radius: 8px;
}

.stat-content {
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

.stat-value {
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--v-theme-on-surface);
  line-height: 1.3;
  word-wrap: break-word;
  overflow-wrap: break-word;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: block;
}

@media (max-width: 600px) {
  .stat-value {
    max-width: 150px;
  }
}

.stat-label {
  font-size: 0.75rem;
  color: var(--v-theme-on-surface-variant);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-top: 2px;
}

.selected-trim-info {
  padding: 8px 0;
}

.trim-price-display {
  font-size: 1.75rem;
  font-weight: 700;
  color: rgb(var(--v-theme-primary));
  text-align: center;
  padding: 8px 0;
}

.trim-highlights {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.highlight-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.875rem;
  color: var(--v-theme-on-surface-variant);
}

.trim-card {
  height: 100%;
  cursor: pointer;
  transition: all 0.3s ease;
  border-radius: 8px;
  overflow: hidden;
}

.trim-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
}

.trim-card.selected {
  border-color: rgb(var(--v-theme-primary));
  border-width: 2px;
}

.trim-card.in-comparison {
  background: rgba(var(--v-theme-success), 0.05);
  border-color: rgb(var(--v-theme-success));
}

.trim-image {
  transition: transform 0.3s ease;
}

.trim-card:hover .trim-image {
  transform: scale(1.05);
}

.trim-card-title {
  font-size: 0.875rem;
  font-weight: 600;
  padding: 12px 16px 4px;
}

.trim-card-price {
  font-size: 1.125rem;
  font-weight: 700;
  color: rgb(var(--v-theme-primary));
  padding: 0 16px;
}

.trim-features-list {
  min-height: 32px;
}

.features-grid {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.feature-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.875rem;
  color: var(--v-theme-on-surface);
}

.spec-value {
  font-weight: 500;
  color: var(--v-theme-on-surface-variant);
  font-size: 0.875rem;
}

.v-theme--dark {
  .hero-overlay {
    background: linear-gradient(
      to bottom,
      rgba(0, 0, 0, 0.5) 0%,
      rgba(0, 0, 0, 0.85) 100%
    );
  }

  .feature-card {
    background: var(--v-theme-surface);
  }

  .color-preview {
    border-color: rgba(255, 255, 255, 0.1);
  }

  .color-option {
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  }

  .stat-item {
    background: rgba(255, 255, 255, 0.05);
  }
}

@media (max-width: 1280px) {
  .hero-title {
    font-size: 2.5rem;
  }

  .hero-subtitle {
    font-size: 1.25rem;
  }
}

@media (max-width: 960px) {
  .hero-title {
    font-size: 2rem;
  }

  .cards-container {
    margin-top: -24px;
  }

  .stats-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 600px) {
  .hero-title {
    font-size: 1.75rem;
  }

  .hero-subtitle {
    font-size: 1.125rem;
  }

  .hero-overlay {
    padding: 32px 0;
  }

  .trim-price-display {
    font-size: 1.5rem;
  }
}

*:focus-visible {
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: 2px;
}

.color-option:focus-visible {
  outline-offset: 4px;
}

@media print {
  .hero-card {
    page-break-after: avoid;
  }

  .feature-card {
    page-break-inside: avoid;
  }
}

.model-comparison-container {
  margin-top: 48px;
  margin-bottom: 48px;
}

.model-comparison-card {
  border-radius: 16px;
  overflow: hidden;
}

.v-theme--dark {
  .model-comparison-card {
    background: #1e1e1e !important;
  }
}
</style>
